// Client-side registration of the portfolio process widget.
//
// A dashboards widget becomes renderable by handing a factory to the library's widget registry.
// Sprint 9 found that a consumer had no way to reach that registry — it was a module-level Map
// inside a hashed, minified chunk with no exported binding and nothing on a global — so the widget
// was built, tested and then withheld from the picker rather than offering a card that could never
// draw. That was handed to the library, and the library has shipped both routes it was asked for:
//
//   · `globalThis.SisolcoDashboards.registerWidget(key, factory)`, installed as a side effect of
//     the catalogue barrel, so any page that mounts a dashboard publishes it; and
//   · `/Sisolco.Dashboards/esm/Modules/Dashboards/Catalogue/index.js`, a real emitted entry point
//     now that the barrel has an `.mts` sibling.
//
// ⚠️⚠️ THE GLOBAL IS USED, THE MODULE URL IS DELIBERATELY NOT — and the reason is size. Importing
// the barrel pulls the catalogue chunk, which is ~1.5 MB (it carries ECharts and all twenty-two
// built-in widgets). On a dashboard page that is already in flight and the import would be free; on
// every other page in the application it is 1.5 MB downloaded so that a widget which will never be
// asked for can be registered. Reading a global costs nothing on either kind of page, so that is
// what this does. The import route stays available if a future need ever justifies it.
//
// ⚠️⚠️ THE ORDERING IS THE WHOLE PROBLEM, AND A NAIVE READ OF THE GLOBAL SILENTLY FAILS. `ScriptInit`
// is a module in `_LayoutHead.cshtml`; the dashboards bootstrap is a module in the page body. Module
// scripts are deferred and execute in document order, so ScriptInit runs FIRST — before the
// catalogue has loaded and therefore before anything has been put on the global. Measured live: at
// ScriptInit time `globalThis.SisolcoDashboards` carried `openDialog` alone, KeyView's registration
// was a no-op, and moments later the library's own twenty-two widgets registered without it. The
// failure is invisible — no error, no console line, just a card that draws its title and nothing
// else, which is exactly the symptom sprint 9's live pass reported.
//
// So registration is DEFERRED to `DOMContentLoaded` when there is no registrar yet:
//
//   · the dashboards bootstrap's own `mountAll` is scheduled the same way, and its listener is added
//     after ours (its module evaluates after ScriptInit), so ours runs first; and
//   · even if that ordering ever reversed, `mountDashboardByKey` awaits an HTTP retrieve of the
//     layout before any cell is created, so registration still lands ahead of the first `createWidget`.
//
// The synchronous attempt is kept ahead of the listener for the case where a host loaded the
// catalogue before ScriptInit, and for any caller that invokes this later by hand.
//
// ⚠️ KeyView's widget sits under KeyView's OWN key, not the contract's. The published engine
// registers a provider under `QAProcessWidgetKeys.PortfolioProgress`, and two providers under one
// key make the dashboards registry fail fast — taking every dashboard in the application down, not
// just this cell. See `KeyViewProcessWidgetKeys`.

import { ProcessPortfolioWidget, ProcessPortfolioWidgetKey } from "./processPortfolioWidget";

/** The registry surface the library publishes on its global. */
export type WidgetRegistrar = (key: string, factory: () => unknown) => void;

interface DashboardsGlobal {
    registerWidget?: WidgetRegistrar;
}

/** What a registration attempt did, so a caller (and a test) can tell the three cases apart. */
export type RegistrationOutcome =
    /** A registrar was there and the factory was handed over. */
    | "registered"
    /** No registrar yet, but the document is still parsing — retried at DOMContentLoaded. */
    | "deferred"
    /** No registrar, and no later point at which one is expected. Not an error. */
    | "unavailable";

/**
 * The registrar, if the host page has one. Reads a global and nothing else — no network, no import,
 * no console output — so calling it on every page costs nothing and says nothing when absent.
 */
export function resolveWidgetRegistrar(scope: unknown = globalThis): WidgetRegistrar | undefined {
    const dashboards = (scope as { SisolcoDashboards?: DashboardsGlobal } | null | undefined)
        ?.SisolcoDashboards;

    return typeof dashboards?.registerWidget === "function"
        ? dashboards.registerWidget.bind(dashboards)
        : undefined;
}

/**
 * Registers the widget factory when there is somewhere to register it, right now.
 *
 * Returns whether it did, so a caller can tell "registered" from "no registrar on this page yet"
 * without either being an error. It is not an error: most pages host no dashboard at all.
 */
export function registerProcessPortfolioWidget(scope: unknown = globalThis): boolean {
    const register = resolveWidgetRegistrar(scope);
    if (!register) return false;

    register(ProcessPortfolioWidgetKey, () => new ProcessPortfolioWidget());
    return true;
}

/**
 * Registers the widget, waiting for the dashboards runtime to publish its registry if it has not
 * done so yet. This is what `ScriptInit` calls; see the ordering note at the top of the file for why
 * the plain synchronous call is not enough.
 *
 * `doc` is a parameter so the deferral can be driven in a test rather than only observed.
 */
export function installProcessPortfolioWidgetRegistration(
    scope: unknown = globalThis,
    doc: Pick<Document, "readyState" | "addEventListener"> | undefined =
        typeof document === "undefined" ? undefined : document,
): RegistrationOutcome {
    if (registerProcessPortfolioWidget(scope)) return "registered";

    // ⚠️ The test is "not complete", NOT "loading", and getting that wrong is a silent no-op that
    // costs the whole feature. A deferred module script — which is what this is — executes AFTER
    // parsing finishes, so `readyState` reads "interactive" here, never "loading". DOMContentLoaded
    // has still not fired at that point (it waits for every deferred script), so it is a real later
    // point and the only one worth taking. Measured: guarding on "loading" alone never defers, and
    // the widget is left unregistered on every page in the application.
    if (doc && doc.readyState !== "complete") {
        doc.addEventListener("DOMContentLoaded", () => { registerProcessPortfolioWidget(scope); },
            { once: true });
        return "deferred";
    }

    return "unavailable";
}
